import { Simulation } from '@/hooks/useSimulation';
import { CurrencyCodeType, DashboardOperation, PaymentRailType } from '@/types/currency';
import { RecipientResponse } from '@/types/recipient';
import { TransactionResponse } from '@/types/transaction';
import { IOFAmount } from '@/utility/IOFAmount';
import { GenericAction } from '@/utility/reducers';

export interface WithdrawalFlowStateBase {
  readonly paymentRail: PaymentRailType;
  readonly fundingMethod: PaymentRailType;
  readonly fromAmount: number | null /* null means: unset */;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number | null;
  readonly toCurrency: CurrencyCodeType;
  readonly simulationId: string | null;
  readonly transactionId: string | null;
  readonly destination: string;
  readonly sameDayAch: boolean;
}

export interface PIXWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly IOF?: {
    readonly label: string;
    readonly amount: IOFAmount;
  };
}

export interface SPEIWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly recipientName: string;
  readonly clabe: string;
  readonly accountNumber: string;
  readonly institution: string;
}

export interface ACHOrWireWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly beneficiaryName: string;
  readonly accountType: 'checking' | 'savings';
  readonly bankName: string;
  readonly bankAddress: string;
  readonly accountNumber: string;
  readonly routingNumber: string;
}

export interface CryptoWithdrawalFlowState extends WithdrawalFlowStateBase {
  readonly chain: string;
  readonly address: string;
}

export type WithdrawalFlowState =
  | PIXWithdrawalFlowState
  | SPEIWithdrawalFlowState
  | ACHOrWireWithdrawalFlowState
  | CryptoWithdrawalFlowState;

export const initialState: WithdrawalFlowState = {
  fundingMethod: PaymentRailType.unset,
  fromAmount: null,
  fromCurrency: CurrencyCodeType.UNSET,
  toAmount: null,
  paymentRail: PaymentRailType.unset,
  toCurrency: CurrencyCodeType.UNSET,
  simulationId: null,
  transactionId: null,
  destination: '',
  sameDayAch: false,
};

export enum WithdrawalFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  fromCurrencyChanged = 'fromCurrencyChanged',
  simulationCreated = 'simulationCreated',
  withdrawalFlowFinished = 'withdrawalFlowFinished',
  transactionCreated = 'transactionCreated',
  destinationChanged = 'destinationChanged',
  sameDayAchChanged = 'sameDayAchChanged',
}

export type TransactionFlowChangedAction = GenericAction<
  WithdrawalFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type DestinationChangedAction = GenericAction<
  WithdrawalFlowActionType.destinationChanged,
  RecipientResponse
>;

export type FromAmountChangedAction = GenericAction<
  WithdrawalFlowActionType.fromAmountChanged,
  number | null
>;

export type ToAmountChangedAction = GenericAction<
  WithdrawalFlowActionType.toAmountChanged,
  number | null
>;

export type FromCurrencyChangedAction = GenericAction<
  WithdrawalFlowActionType.fromCurrencyChanged,
  CurrencyCodeType
>;

export type SimulationCreatedAction = GenericAction<
  WithdrawalFlowActionType.simulationCreated,
  Simulation
>;

export type ResetWithdrawalFlowAction = GenericAction<
  WithdrawalFlowActionType.withdrawalFlowFinished,
  void
>;

export type TransactionCreatedAction = GenericAction<
  WithdrawalFlowActionType.transactionCreated,
  TransactionResponse
>;

export type SameDayAchChangedAction = GenericAction<
  WithdrawalFlowActionType.sameDayAchChanged,
  boolean
>;

export type WithdrawalFlowAction =
  | TransactionFlowChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | SimulationCreatedAction
  | ResetWithdrawalFlowAction
  | TransactionCreatedAction
  | DestinationChangedAction
  | FromCurrencyChangedAction
  | SameDayAchChangedAction;

export const withdrawalFlowReducer = (
  state: WithdrawalFlowState,
  action: WithdrawalFlowAction,
): WithdrawalFlowState => {
  switch (action.type) {
    case WithdrawalFlowActionType.transactionFlowChanged: {
      const { currency, destinations } = action.payload;

      return {
        ...state,
        fromAmount: null,
        fromCurrency: currency,
        toCurrency: destinations[0].currency ?? CurrencyCodeType.UNSET,
        fundingMethod:
          destinations.length === 0
            ? PaymentRailType.unset
            : destinations[0].fundingMethods?.length
              ? destinations[0].fundingMethods[0]
              : PaymentRailType.unset,
      };
    }
    case WithdrawalFlowActionType.fromAmountChanged: {
      const amount = action.payload;
      return {
        ...state,
        toAmount: null,
        simulationId: null,
        transactionId: null,
        fromAmount: amount,
      };
    }
    case WithdrawalFlowActionType.toAmountChanged: {
      const amount = action.payload;
      return {
        ...state,
        fromAmount: null,
        simulationId: null,
        transactionId: null,
        toAmount: amount,
      };
    }
    case WithdrawalFlowActionType.simulationCreated: {
      const { id, to } = action.payload;
      return {
        ...state,
        simulationId: id,
        toAmount: to.value,
      };
    }
    case WithdrawalFlowActionType.withdrawalFlowFinished: {
      return {
        ...state,
        fundingMethod: PaymentRailType.unset,
        fromAmount: null,
        fromCurrency: CurrencyCodeType.UNSET,
        toAmount: null,
        toCurrency: CurrencyCodeType.UNSET,
        simulationId: null,
        transactionId: null,
      };
    }
    case WithdrawalFlowActionType.fromCurrencyChanged:
      return {
        ...state,
        fromCurrency: action.payload,
      };
    case WithdrawalFlowActionType.transactionCreated: {
      const { id } = action.payload;
      return {
        ...state,
        transactionId: id,
      };
    }
    case WithdrawalFlowActionType.destinationChanged: {
      return {
        ...state,
        paymentRail: action.payload.type,
        destination: action.payload.id,
        sameDayAch: false, // Reset when destination changes
      };
    }
    case WithdrawalFlowActionType.sameDayAchChanged: {
      return {
        ...state,
        sameDayAch: action.payload,
      };
    }
    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: TransactionFlowChangedAction['payload'],
): TransactionFlowChangedAction => ({
  type: WithdrawalFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const fromAmountChanged = (
  payload: FromAmountChangedAction['payload'],
): FromAmountChangedAction => ({
  type: WithdrawalFlowActionType.fromAmountChanged,
  payload: payload,
});

export const toAmountChanged = (
  payload: ToAmountChangedAction['payload'],
): ToAmountChangedAction => ({
  type: WithdrawalFlowActionType.toAmountChanged,
  payload: payload,
});

export const fromCurrencyChanged = (
  payload: FromCurrencyChangedAction['payload'],
): FromCurrencyChangedAction => ({
  type: WithdrawalFlowActionType.fromCurrencyChanged,
  payload: payload,
});

export const destinationChanged = (payload: RecipientResponse): DestinationChangedAction => ({
  type: WithdrawalFlowActionType.destinationChanged,
  payload: payload,
});

export const sameDayAchChanged = (payload: boolean): SameDayAchChangedAction => ({
  type: WithdrawalFlowActionType.sameDayAchChanged,
  payload: payload,
});
